import React, {useEffect, useState} from 'react';
import styles from './geometry-editor-window.module.scss';
import { useAppSelector } from "../../../../hooks/useAppSelector";
import {
    setMainOperationFeature,
    setSecondaryOperationFeature
} from "../../../../services/reducers/utilities-journal";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import * as projection from "@arcgis/core/geometry/projection";
import {SelectedFeature} from "../../../../interfaces/utilities";
import Graphic from "@arcgis/core/Graphic";
import { triggerGeometryOperation } from "../../../../services/actions/map";
import { isProtectedGraphic } from '../../../../utils/helpers-for-utilities';
import {useTranslation} from "react-i18next";

export type OperationType = 'merge' | 'subtract';

const GeometryEditorWindow = () => {
    const [mode, setMode] = useState<OperationType | null>(null);
    const [isValidGeometryPair, setIsValidGeometryPair] = useState<boolean>(true);
    const {
        selectedFeature,
        mainOperationFeature,
        secondaryOperationFeature,
        selectedRow,
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        convertedGeometry
    } = useAppSelector(state => state.masterUtilities);

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    useEffect(() => {
        const checkAndProject = async () => {
            if (!mainOperationFeature || !secondaryOperationFeature) {
                setIsValidGeometryPair(true);
                return;
            }

            const mainGeom = mainOperationFeature.graphic.geometry;
            let secondGeom = secondaryOperationFeature.graphic.geometry;

            if (!mainGeom || !secondGeom) {
                setIsValidGeometryPair(false);
                return;
            }

            const mainSR = mainGeom.spatialReference;
            const secondSR = secondGeom.spatialReference;

            if (mainSR.wkid !== secondSR.wkid) {
                try {
                    const result = await projection.project(secondGeom, mainSR);
                    const projectedSecondGeom = Array.isArray(result) ? result[0] : result;

                    const originalGraphic = secondaryOperationFeature.graphic;

                    const updatedGraphic = new Graphic({
                        geometry: projectedSecondGeom,
                        attributes: originalGraphic.attributes,
                        symbol: originalGraphic.symbol,
                    });

                    const updatedSecondary: SelectedFeature = {
                        ...secondaryOperationFeature,
                        graphic: updatedGraphic
                    };

                    dispatch(setSecondaryOperationFeature(updatedSecondary));
                    secondGeom = projectedSecondGeom;
                } catch (error) {
                    console.error(error);
                    setIsValidGeometryPair(false);
                    return;
                }
            }

            const sameType = mainOperationFeature.isArea === secondaryOperationFeature.isArea;

            if (!sameType) {
                setIsValidGeometryPair(false);
                return;
            }

            if (mode === 'subtract') {
                const intersects = geometryEngine.intersects(mainGeom, secondGeom);
                setIsValidGeometryPair(intersects);
            } else {
                setIsValidGeometryPair(true);
            }
        };

        checkAndProject();
    }, [mainOperationFeature, secondaryOperationFeature, mode]);

    useEffect(() => {
        if (
            mainOperationFeature &&
            isProtectedGraphic(
                mainOperationFeature,
                stageTerritoryGeometry,
                buildingTerritoryGeometry,
                selectedRow!,
                convertedGeometry
            )
        ) {
            dispatch(setMainOperationFeature(null));
        }
    }, [
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        convertedGeometry,
        selectedRow,
        mainOperationFeature
    ]);

    const handleConfirm = () => {
        if (!selectedFeature) return;

        if (!mainOperationFeature) {
            dispatch(setMainOperationFeature(selectedFeature));
        } else if (!secondaryOperationFeature) {
            dispatch(setSecondaryOperationFeature(selectedFeature));
        } else if (mainOperationFeature && secondaryOperationFeature && mode) {
            dispatch(triggerGeometryOperation(mode))
            setMode(null)
        }
    };

    const handleResetMode = () => {
        setMode(null);
        dispatch(setMainOperationFeature(null));
        dispatch(setSecondaryOperationFeature(null));
    };

    const bothSelected = mainOperationFeature && secondaryOperationFeature;

    const instructions: Record<OperationType, string> = {
        merge: bothSelected
            ? t('geometryEditor.instructions.bothSelected')
            : (
                mainOperationFeature
                    ? t('geometryEditor.instructions.selectSecondMerge')
                    : t('geometryEditor.instructions.selectFirstMerge')
            ),
        subtract: bothSelected
            ? t('geometryEditor.instructions.bothSelected')
            : (
                mainOperationFeature
                    ? t('geometryEditor.instructions.selectSecondSubtract')
                    : t('geometryEditor.instructions.selectFirstSubtract')
            ),
    };

    const confirmButtonText = bothSelected
        ? t(`geometryEditor.confirm.${mode}`)
        : t('geometryEditor.confirm.default');

    const isProtected =
        !mainOperationFeature &&
        selectedFeature &&
        isProtectedGraphic(
            selectedFeature!,
            stageTerritoryGeometry,
            buildingTerritoryGeometry,
            selectedRow!,
            convertedGeometry
        );

    const confirmDisabled =
        !selectedFeature ||
        !selectedFeature.isArea ||
        isProtected ||
        (mainOperationFeature &&
            !secondaryOperationFeature &&
            selectedFeature.isArea !== mainOperationFeature.isArea) ||
        (mainOperationFeature &&
            secondaryOperationFeature &&
            !isValidGeometryPair);

    return (
        <div className={styles.geometryEditorWindow}>
            {mode ? (
                <div className={styles.instructions}>
                    <span className={styles.title}>{instructions[mode]}</span>
                    <div className={styles.groupBtn}>
                        <button
                            onClick={handleConfirm}
                            className={styles.confirmBtn}
                            disabled={!!confirmDisabled}
                        >
                            <div className={styles.confirmIcon} />
                            {confirmButtonText}
                        </button>
                        <button onClick={handleResetMode} className={styles.cancelBtn}>
                            <div className={styles.cancelIcon} />
                            {t('geometryEditor.cancel')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className={styles.groupBtn}>
                    <button onClick={() => setMode('merge')} className={styles.btn}>
                        <div className={styles.mergeIcon} />
                        {t('geometryEditor.merge')}
                    </button>
                    <button onClick={() => setMode('subtract')} className={styles.btn}>
                        <div className={styles.subtractIcon} />
                        {t('geometryEditor.subtract')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default GeometryEditorWindow;
